<template>
  <div class="pengaturan-notifikasi-container p-4 md:p-6">
    <!-- Header -->
    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 mb-2">Pengaturan Notifikasi</h1>
      <p class="text-gray-600">Kelola preferensi notifikasi untuk berbagai aktivitas sistem</p>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>

    <!-- Main Content -->
    <div v-else class="space-y-6">
      <!-- Metode Notifikasi Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Metode Notifikasi</h2>
            <p class="text-sm text-gray-600 mt-1">Pilih cara Anda menerima notifikasi</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <!-- Email Notification -->
          <div class="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
            <div class="flex items-start flex-1">
              <div class="p-2 bg-blue-100 rounded-lg mr-3">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="font-semibold text-gray-800">Email Notifikasi</h3>
                <p class="text-sm text-gray-600 mt-1">Terima notifikasi melalui email</p>
              </div>
            </div>
            <el-switch
              v-model="notificationSettings.emailNotification"
              :disabled="saving.notification"
            />
          </div>

          <!-- Push Notification -->
          <div class="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
            <div class="flex items-start flex-1">
              <div class="p-2 bg-green-100 rounded-lg mr-3">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="font-semibold text-gray-800">Push Notification</h3>
                <p class="text-sm text-gray-600 mt-1">Terima notifikasi push di browser atau aplikasi mobile</p>
              </div>
            </div>
            <el-switch
              v-model="notificationSettings.pushNotification"
              :disabled="saving.notification"
            />
          </div>

          <!-- SMS Notification -->
          <div class="flex items-start justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition">
            <div class="flex items-start flex-1">
              <div class="p-2 bg-purple-100 rounded-lg mr-3">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="font-semibold text-gray-800">SMS Notifikasi</h3>
                <p class="text-sm text-gray-600 mt-1">Terima notifikasi penting via SMS</p>
              </div>
            </div>
            <el-switch
              v-model="notificationSettings.smsNotification"
              :disabled="saving.notification"
            />
          </div>
        </div>
      </div>

      <!-- Notifikasi Absensi Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Notifikasi Absensi</h2>
            <p class="text-sm text-gray-600 mt-1">Pengaturan notifikasi terkait kehadiran</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="flex items-start justify-between p-4 border-l-4 border-blue-500 bg-blue-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Reminder Absen Masuk</h3>
              <p class="text-sm text-gray-600 mt-1">Pengingat untuk melakukan absen masuk</p>
            </div>
            <el-switch
              v-model="notificationSettings.checkInReminder"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-orange-500 bg-orange-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Reminder Absen Pulang</h3>
              <p class="text-sm text-gray-600 mt-1">Pengingat untuk melakukan absen pulang</p>
            </div>
            <el-switch
              v-model="notificationSettings.checkOutReminder"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-red-500 bg-red-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Notifikasi Keterlambatan</h3>
              <p class="text-sm text-gray-600 mt-1">Pemberitahuan jika terlambat absen masuk</p>
            </div>
            <el-switch
              v-model="notificationSettings.lateNotification"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-yellow-500 bg-yellow-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Notifikasi Absen Tidak Lengkap</h3>
              <p class="text-sm text-gray-600 mt-1">Pemberitahuan jika belum melakukan check-out</p>
            </div>
            <el-switch
              v-model="notificationSettings.incompleteAttendanceNotification"
              :disabled="saving.notification"
            />
          </div>
        </div>
      </div>

      <!-- Notifikasi Pengumuman & Update Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Pengumuman & Update</h2>
            <p class="text-sm text-gray-600 mt-1">Notifikasi untuk pengumuman dan pembaruan</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="flex items-start justify-between p-4 border-l-4 border-indigo-500 bg-indigo-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Pengumuman Baru</h3>
              <p class="text-sm text-gray-600 mt-1">Notifikasi saat ada pengumuman baru dari perusahaan</p>
            </div>
            <el-switch
              v-model="notificationSettings.announcementNotification"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-pink-500 bg-pink-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Update Aplikasi</h3>
              <p class="text-sm text-gray-600 mt-1">Notifikasi saat ada versi baru aplikasi mobile</p>
            </div>
            <el-switch
              v-model="notificationSettings.appUpdateNotification"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-teal-500 bg-teal-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Berita & Artikel</h3>
              <p class="text-sm text-gray-600 mt-1">Notifikasi untuk berita dan artikel terbaru</p>
            </div>
            <el-switch
              v-model="notificationSettings.newsNotification"
              :disabled="saving.notification"
            />
          </div>
        </div>
      </div>

      <!-- Notifikasi WFH & Cuti Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">WFH & Cuti</h2>
            <p class="text-sm text-gray-600 mt-1">Notifikasi terkait permohonan dan persetujuan</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="flex items-start justify-between p-4 border-l-4 border-cyan-500 bg-cyan-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Persetujuan WFH</h3>
              <p class="text-sm text-gray-600 mt-1">Notifikasi status permohonan Work From Home</p>
            </div>
            <el-switch
              v-model="notificationSettings.wfhApprovalNotification"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-lime-500 bg-lime-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Persetujuan Cuti</h3>
              <p class="text-sm text-gray-600 mt-1">Notifikasi status permohonan cuti/izin</p>
            </div>
            <el-switch
              v-model="notificationSettings.leaveApprovalNotification"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-emerald-500 bg-emerald-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Reminder WFH</h3>
              <p class="text-sm text-gray-600 mt-1">Pengingat jadwal Work From Home yang sudah disetujui</p>
            </div>
            <el-switch
              v-model="notificationSettings.wfhReminderNotification"
              :disabled="saving.notification"
            />
          </div>
        </div>
      </div>

      <!-- Notifikasi Sistem Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Notifikasi Sistem</h2>
            <p class="text-sm text-gray-600 mt-1">Notifikasi terkait aktivitas sistem</p>
          </div>
        </div>
        
        <div class="space-y-4">
          <div class="flex items-start justify-between p-4 border-l-4 border-amber-500 bg-amber-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Aktivitas Keamanan</h3>
              <p class="text-sm text-gray-600 mt-1">Notifikasi login, perubahan password, dll</p>
            </div>
            <el-switch
              v-model="notificationSettings.securityNotification"
              :disabled="saving.notification"
            />
          </div>

          <div class="flex items-start justify-between p-4 border-l-4 border-rose-500 bg-rose-50 rounded">
            <div class="flex-1">
              <h3 class="font-medium text-gray-800">Maintenance Sistem</h3>
              <p class="text-sm text-gray-600 mt-1">Pemberitahuan saat sistem dalam maintenance</p>
            </div>
            <el-switch
              v-model="notificationSettings.maintenanceNotification"
              :disabled="saving.notification"
            />
          </div>
        </div>
      </div>

      <!-- Jadwal Notifikasi Card -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-start justify-between mb-4">
          <div>
            <h2 class="text-lg font-semibold text-gray-800">Jadwal Notifikasi</h2>
            <p class="text-sm text-gray-600 mt-1">Atur waktu penerimaan notifikasi</p>
          </div>
        </div>
        
        <el-form 
          :model="notificationSettings" 
          label-width="200px" 
          label-position="left"
          class="settings-form"
        >
          <el-form-item label="Mode Diam">
            <el-switch
              v-model="notificationSettings.quietMode"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.notification"
            />
            <div class="form-hint">Nonaktifkan notifikasi pada jam tertentu</div>
          </el-form-item>

          <div v-if="notificationSettings.quietMode" class="pl-8 space-y-4">
            <el-form-item label="Jam Mulai">
              <el-time-picker
                v-model="notificationSettings.quietModeStart"
                placeholder="Pilih jam mulai"
                format="HH:mm"
                value-format="HH:mm"
                :disabled="saving.notification"
              />
            </el-form-item>

            <el-form-item label="Jam Selesai">
              <el-time-picker
                v-model="notificationSettings.quietModeEnd"
                placeholder="Pilih jam selesai"
                format="HH:mm"
                value-format="HH:mm"
                :disabled="saving.notification"
              />
            </el-form-item>
          </div>

          <el-form-item label="Ringkasan Harian">
            <el-switch
              v-model="notificationSettings.dailySummary"
              active-text="Aktif"
              inactive-text="Nonaktif"
              :disabled="saving.notification"
            />
            <div class="form-hint">Terima ringkasan notifikasi sekali sehari</div>
          </el-form-item>

          <el-form-item v-if="notificationSettings.dailySummary" label="Waktu Ringkasan">
            <el-time-picker
              v-model="notificationSettings.dailySummaryTime"
              placeholder="Pilih waktu"
              format="HH:mm"
              value-format="HH:mm"
              :disabled="saving.notification"
            />
          </el-form-item>
        </el-form>
      </div>

      <!-- Quick Actions -->
      <div class="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg shadow-md p-6 border border-blue-200">
        <h3 class="text-lg font-semibold text-gray-800 mb-4">Aksi Cepat</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button 
            @click="enableAll"
            :disabled="saving.notification"
            class="p-4 bg-white rounded-lg shadow hover:shadow-md transition border border-green-200 hover:border-green-400"
          >
            <div class="text-green-600 font-medium">✓ Aktifkan Semua</div>
            <div class="text-sm text-gray-600 mt-1">Terima semua notifikasi</div>
          </button>

          <button 
            @click="disableAll"
            :disabled="saving.notification"
            class="p-4 bg-white rounded-lg shadow hover:shadow-md transition border border-red-200 hover:border-red-400"
          >
            <div class="text-red-600 font-medium">✕ Nonaktifkan Semua</div>
            <div class="text-sm text-gray-600 mt-1">Matikan semua notifikasi</div>
          </button>

          <button 
            @click="setDefault"
            :disabled="saving.notification"
            class="p-4 bg-white rounded-lg shadow hover:shadow-md transition border border-gray-200 hover:border-gray-400"
          >
            <div class="text-gray-600 font-medium">↺ Pengaturan Default</div>
            <div class="text-sm text-gray-600 mt-1">Kembalikan ke default</div>
          </button>
        </div>
      </div>

      <!-- Action Buttons -->
      <div class="bg-white rounded-lg shadow-md p-6">
        <div class="flex items-center justify-between">
          <div>
            <p class="text-gray-700 font-medium">Simpan Perubahan</p>
            <p class="text-sm text-gray-600">Pastikan pengaturan notifikasi sudah sesuai</p>
          </div>
          
          <div class="flex gap-3">
            <el-button @click="resetSettings" :disabled="saving.notification">
              Reset
            </el-button>
            <el-button 
              type="primary" 
              @click="saveNotificationSettings" 
              :loading="saving.notification"
            >
              Simpan Perubahan
            </el-button>
          </div>
        </div>
      </div>
    </div>

    <!-- Last Updated Info -->
    <div v-if="lastUpdated" class="mt-4 text-center text-sm text-gray-500">
      Terakhir diupdate: {{ formatDate(lastUpdated) }}
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import Axios from '@/utils/axiosConfig';

export default {
  name: "PengaturanNotifikasiView",
  data() {
    return {
      loading: false,
      saving: {
        notification: false
      },
      // Notification Settings Data
      notificationSettings: {
        // Metode
        emailNotification: true,
        pushNotification: true,
        smsNotification: false,
        
        // Absensi
        checkInReminder: true,
        checkOutReminder: true,
        lateNotification: true,
        incompleteAttendanceNotification: true,
        
        // Pengumuman & Update
        announcementNotification: true,
        appUpdateNotification: true,
        newsNotification: false,
        
        // WFH & Cuti
        wfhApprovalNotification: true,
        leaveApprovalNotification: true,
        wfhReminderNotification: true,
        
        // Sistem
        securityNotification: true,
        maintenanceNotification: true,
        
        // Jadwal
        quietMode: false,
        quietModeStart: '22:00',
        quietModeEnd: '07:00',
        dailySummary: false,
        dailySummaryTime: '18:00'
      },
      originalSettings: {},
      lastUpdated: null
    };
  },
  computed: {
    ...mapState(['host'])
  },
  mounted() {
    this.loadSettings();
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const response = await Axios.get(`${this.host}/api/settings/notification`);
        
        if (response.data) {
          const data = response.data.data || response.data;
          this.notificationSettings = {
            ...this.notificationSettings,
            ...data
          };
          // Save original settings for reset
          this.originalSettings = JSON.parse(JSON.stringify(this.notificationSettings));
          this.lastUpdated = data.updatedAt || null;
        }
      } catch (error) {
        console.error('Error loading settings:', error);
        this.$message.warning('Gagal memuat pengaturan. Menggunakan nilai default.');
      } finally {
        this.loading = false;
      }
    },

    async saveNotificationSettings() {
      // Validation
      if (this.notificationSettings.quietMode) {
        if (!this.notificationSettings.quietModeStart || !this.notificationSettings.quietModeEnd) {
          this.$message.error('Jam mode diam harus diisi');
          return;
        }
      }

      if (this.notificationSettings.dailySummary && !this.notificationSettings.dailySummaryTime) {
        this.$message.error('Waktu ringkasan harian harus diisi');
        return;
      }

      // Check if at least one notification method is enabled
      if (!this.notificationSettings.emailNotification && 
          !this.notificationSettings.pushNotification && 
          !this.notificationSettings.smsNotification) {
        this.$confirm(
          'Tidak ada metode notifikasi yang aktif. Anda tidak akan menerima notifikasi apapun. Lanjutkan?',
          'Peringatan',
          {
            confirmButtonText: 'Ya, Lanjutkan',
            cancelButtonText: 'Batal',
            type: 'warning'
          }
        ).then(() => {
          this.performSave();
        }).catch(() => {
          // User cancelled
        });
        return;
      }

      this.performSave();
    },

    async performSave() {
      this.saving.notification = true;
      try {
        const response = await Axios.post(`${this.host}/api/settings/notification`, this.notificationSettings);
        
        if (response.data) {
          this.lastUpdated = new Date();
          this.originalSettings = JSON.parse(JSON.stringify(this.notificationSettings));
          this.$message.success('Pengaturan notifikasi berhasil disimpan!');
        }
      } catch (error) {
        console.error('Error saving notification settings:', error);
        this.$message.error('Gagal menyimpan pengaturan notifikasi');
      } finally {
        this.saving.notification = false;
      }
    },

    resetSettings() {
      this.$confirm('Apakah Anda yakin ingin mereset semua perubahan?', 'Konfirmasi', {
        confirmButtonText: 'Ya, Reset',
        cancelButtonText: 'Batal',
        type: 'warning'
      }).then(() => {
        this.notificationSettings = JSON.parse(JSON.stringify(this.originalSettings));
        this.$message.info('Pengaturan direset ke nilai sebelumnya');
      }).catch(() => {
        // User cancelled
      });
    },

    enableAll() {
      this.$confirm('Aktifkan semua notifikasi?', 'Konfirmasi', {
        confirmButtonText: 'Ya, Aktifkan',
        cancelButtonText: 'Batal',
        type: 'info'
      }).then(() => {
        // Set all notification switches to true
        Object.keys(this.notificationSettings).forEach(key => {
          if (typeof this.notificationSettings[key] === 'boolean') {
            this.notificationSettings[key] = true;
          }
        });
        this.$message.success('Semua notifikasi diaktifkan');
      }).catch(() => {
        // User cancelled
      });
    },

    disableAll() {
      this.$confirm('Nonaktifkan semua notifikasi? Anda tidak akan menerima notifikasi apapun.', 'Peringatan', {
        confirmButtonText: 'Ya, Nonaktifkan',
        cancelButtonText: 'Batal',
        type: 'warning'
      }).then(() => {
        // Set all notification switches to false
        Object.keys(this.notificationSettings).forEach(key => {
          if (typeof this.notificationSettings[key] === 'boolean') {
            this.notificationSettings[key] = false;
          }
        });
        this.$message.info('Semua notifikasi dinonaktifkan');
      }).catch(() => {
        // User cancelled
      });
    },

    setDefault() {
      this.$confirm('Kembalikan ke pengaturan default?', 'Konfirmasi', {
        confirmButtonText: 'Ya, Kembalikan',
        cancelButtonText: 'Batal',
        type: 'info'
      }).then(() => {
        // Reset to default values
        this.notificationSettings = {
          emailNotification: true,
          pushNotification: true,
          smsNotification: false,
          checkInReminder: true,
          checkOutReminder: true,
          lateNotification: true,
          incompleteAttendanceNotification: true,
          announcementNotification: true,
          appUpdateNotification: true,
          newsNotification: false,
          wfhApprovalNotification: true,
          leaveApprovalNotification: true,
          wfhReminderNotification: true,
          securityNotification: true,
          maintenanceNotification: true,
          quietMode: false,
          quietModeStart: '22:00',
          quietModeEnd: '07:00',
          dailySummary: false,
          dailySummaryTime: '18:00'
        };
        this.$message.success('Pengaturan dikembalikan ke default');
      }).catch(() => {
        // User cancelled
      });
    },

    formatDate(date) {
      if (!date) return '';
      
      const d = new Date(date);
      const options = { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      
      return d.toLocaleDateString('id-ID', options);
    }
  }
};
</script>

<style scoped>
.pengaturan-notifikasi-container {
  max-width: 1200px;
  margin: 0 auto;
}

.settings-form {
  margin-top: 0;
}

.form-hint {
  margin-top: 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.4;
}

/* Customize Element UI form items */
:deep(.el-form-item) {
  margin-bottom: 24px;
}

:deep(.el-form-item__label) {
  color: #374151;
  font-weight: 500;
}

:deep(.el-time-picker) {
  width: 100%;
}

:deep(.el-switch) {
  height: auto;
}

:deep(.el-switch__label) {
  font-size: 14px;
}

/* Button hover effects */
button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pengaturan-notifikasi-container {
    padding: 1rem;
  }

  :deep(.el-form-item) {
    flex-direction: column;
  }

  :deep(.el-form-item__label) {
    text-align: left !important;
    width: 100% !important;
    margin-bottom: 8px;
  }

  :deep(.el-form-item__content) {
    margin-left: 0 !important;
  }
}
</style>